import {
    Add,
    ArrowDropDown,
    ArrowDropUp,
    PlaylistAdd,
} from '@mui/icons-material'
import {
    Button,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
} from '@mui/material'
import { RosterType } from '__generated__/graphql'
import React, { FC } from 'react'
import RosterDrawer from '../../../components/RosterDrawer'

interface SendToSearchProps {
    selectedCandidates: string[]
    buttonVariant?: 'text' | 'outlined' | 'contained'
}

/**
 * "Send to Roster" button that opens a menu with options to add a candidate to an existing roster, create a new roster, or create a new draft roster. Also includes the drawer which opens upon selecting a menu option with the inputs for creating/adding to a roster.
 *
 * @param props - gridApi
 * @returns ReactElement
 */
const SendToSearch: FC<SendToSearchProps> = ({
    selectedCandidates,
    buttonVariant = 'contained',
}) => {
    const [menuOpen, setMenuOpen] = React.useState(false)
    const [openDrawerType, setOpenDrawerType] = React.useState<
        'existing' | 'new' | undefined
    >(undefined)
    const anchorRef = React.useRef<HTMLButtonElement>(null)

    const handleToggle = () => {
        setMenuOpen((prevOpen) => !prevOpen)
    }

    return (
        <>
            <Button
                data-testid="send-to-button"
                ref={anchorRef}
                size="small"
                aria-controls={menuOpen ? 'roster-button-menu' : undefined}
                aria-expanded={menuOpen ? 'true' : undefined}
                aria-haspopup="menu"
                onClick={handleToggle}
                disabled={selectedCandidates.length === 0}
                variant="outlined"
                sx={{
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    py: 0.5,
                    px: 1.5,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    '&:hover': {
                        borderColor: 'text.secondary',
                        bgcolor: 'action.hover',
                    },
                    '&.Mui-disabled': {
                        borderColor: 'divider',
                        color: 'text.disabled',
                    },
                }}
            >
                Send To
                {menuOpen ? (
                    <ArrowDropUp sx={{ fontSize: '1.25rem', ml: 0.5 }} />
                ) : (
                    <ArrowDropDown sx={{ fontSize: '1.25rem', ml: 0.5 }} />
                )}
            </Button>
            <Menu
                sx={{ zIndex: 1 }}
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                anchorEl={anchorRef.current}
                role={undefined}
                disablePortal
            >
                <MenuItem
                    data-testid="send-to-existing-search"
                    onClick={() => {
                        setMenuOpen(false)
                        setOpenDrawerType('existing')
                    }}
                >
                    <ListItemIcon>
                        <PlaylistAdd />
                    </ListItemIcon>
                    <ListItemText>Existing Search </ListItemText>
                </MenuItem>
                <MenuItem
                    data-testid="send-to-new-search"
                    onClick={() => {
                        setMenuOpen(false)
                        setOpenDrawerType('new')
                    }}
                >
                    <ListItemIcon>
                        <Add />
                    </ListItemIcon>
                    <ListItemText>New Search </ListItemText>
                </MenuItem>
            </Menu>
            <RosterDrawer
                selectedCandidates={selectedCandidates}
                type={openDrawerType}
                presetRosterType={RosterType.Search}
                onClose={() => {
                    setOpenDrawerType(undefined)
                }}
            />
        </>
    )
}

export default SendToSearch
